import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  UserCircle, Plus, X, Save, Phone, Mail, MapPin, Search,
  ShoppingBag, Gem, TrendingUp, Calendar, Edit2, FileText,
  ChevronRight, Star, Clock, Package, Store, CreditCard, Tag,
  Building2, Wallet, CircleDollarSign, Weight, AlertCircle, CheckCircle2, Wrench
} from 'lucide-react';
import { API_URL, getServerUrl } from '../../services/api';

const API = API_URL;

// Stats client calculees depuis achatsData (table ventes, /clients/<id>/achats),
// la source reelle des achats du client. Montant par ligne : montant_final si
// rempli et > 0, sinon prix_vente.
const computeVentesStats = (achats = []) => {
  const list = achats || [];
  const total = list.reduce((s, a) => {
    const mf = Number(a.montant_final || 0);
    const pv = Number(a.prix_vente || 0);
    return s + (mf > 0 ? mf : pv);
  }, 0);
  const nb = list.length;
  return { nb, total, avg: nb ? total / nb : 0 };
};

function ClientsAvance() {
  const [clients, setClients]               = useState([]);
  const [searchTerm, setSearchTerm]         = useState('');
  const [selectedId, setSelectedId]         = useState(null);
  const [detail, setDetail]                 = useState(null);
  const detailRef = useRef(null);
  const [loadingDetail, setLoadingDetail]   = useState(false);
  const [editMode, setEditMode]             = useState(false);
  const [editData, setEditData]             = useState({});
  const [saving, setSaving]                 = useState(false);
  const [showCreate, setShowCreate]         = useState(false);
  const [newClient, setNewClient]           = useState({ nom:'', telephone:'', email:'', adresse:'', ville:'', notes:'', type_client:'particulier', nom_entreprise:'', ice:'', rc:'', if_number:'' });
  const [activeTab, setActiveTab]           = useState('achats_new'); // 'achats_new' | 'fabrications' | 'notes' | 'compte' | 'sav'
  const [compteData, setCompteData]         = useState(null);
  const [achatsData, setAchatsData]         = useState(null);
  const [savData, setSavData]               = useState(null);
  const [openForm, setOpenForm]             = useState(null); // 'depot_or' | 'depot_argent' | 'credit' | 'encaisser_dette' | 'retirer_avoir'
  const [formData, setFormData]             = useState({});
  const [submitting, setSubmitting]         = useState(false);
  const [boutiques, setBoutiques]           = useState([]);
  // Patch 42 — Encaisser solde sur facture
  const [soldeVente, setSoldeVente]         = useState(null);   // achat en cours d'encaissement
  const [soldeForm, setSoldeForm]           = useState({});
  const [soldeSubmitting, setSoldeSubmitting] = useState(false);
  const [soldeError, setSoldeError]         = useState(null);

  const getHeaders = () => {
    const t = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(t ? { 'Authorization': `Bearer ${t}` } : {}) };
  };

  useEffect(() => { loadClients(); }, []);

  // C4 Pass C : charger les boutiques pour les sélecteurs des modals compte client
  useEffect(() => {
    fetch(`${API}/boutiques`, { headers: getHeaders() })
      .then(r => r.json())
      .then(list => Array.isArray(list) && setBoutiques(list.filter(b => b.type !== 'central')))
      .catch(() => {});
  }, []);

  const loadClients = async () => {
    try {
      const res = await fetch(`${API}/clients`);
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    setDetail(null);
    try {
      const res = await fetch(`${API}/clients/${id}/historique`);
      if (res.ok) setDetail(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  }, []);

  const loadCompte = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/clients/${id}/compte`, { headers: getHeaders() });
      if (res.ok) setCompteData(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const [mouvementsCompte, setMouvementsCompte] = useState([]);
  const loadAchatsNew = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/clients/${id}/achats`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Nouveau format: {achats: [], mouvements_compte: []}
        if (data.achats) {
          setAchatsData(data.achats);
          setMouvementsCompte(data.mouvements_compte || []);
        } else {
          // Ancien format: array directe
          setAchatsData(Array.isArray(data) ? data : []);
        }
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadSav = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/clients/${id}/sav`, { headers: getHeaders() });
      if (res.ok) setSavData(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const handleCompteAction = async (type) => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const endpoints = {
        depot_or: 'depot-or',
        depot_argent: 'depot-argent',
        credit: 'credit',
        encaisser_dette: 'encaisser-dette',
        retirer_avoir: 'retirer-avoir'
      };
      const res = await fetch(`${API}/clients/${selectedId}/${endpoints[type]}`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(formData)
      });
      if (res.ok) {
        setOpenForm(null);
        setFormData({});
        // Refresh complet : liste + fiche détail + compte + historique achats/mvts
        await Promise.all([
          loadClients(),
          loadDetail(selectedId),
          loadCompte(selectedId),
          loadAchatsNew(selectedId),
        ]);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || err.message || 'Erreur lors de l\'opération');
      }
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  // Patch 42 — Encaisser solde sur facture
  const openSoldeModal = (achat) => {
    setSoldeVente(achat);
    setSoldeError(null);
    setSoldeForm({
      montant: achat.dette_restante || 0,
      mode_paiement: 'especes',
      boutique_id: '',
      reference: '',
      commentaire: ''
    });
  };
  const closeSoldeModal = () => {
    setSoldeVente(null);
    setSoldeForm({});
    setSoldeError(null);
  };
  const handleEncaisserSolde = async () => {
    if (!soldeVente) return;
    setSoldeError(null);
    const m = parseFloat(soldeForm.montant);
    if (!m || m <= 0) { setSoldeError('Montant invalide'); return; }
    if (!soldeForm.mode_paiement) { setSoldeError('Mode de paiement requis'); return; }
    const isAvoir = soldeForm.mode_paiement === 'avoir';
    if (!isAvoir && !soldeForm.boutique_id) { setSoldeError('Boutique requise'); return; }
    // En mode avoir : on n'envoie pas boutique_id (backend l'accepte null/absent)
    const payload = isAvoir
      ? { montant: soldeForm.montant, mode_paiement: 'avoir', reference: soldeForm.reference, commentaire: soldeForm.commentaire }
      : soldeForm;
    setSoldeSubmitting(true);
    try {
      const res = await fetch(`${API}/ventes/${soldeVente.id}/encaisser-solde`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        closeSoldeModal();
        await Promise.all([
          loadClients(),
          loadDetail(selectedId),
          loadCompte(selectedId),
          loadAchatsNew(selectedId),
        ]);
      } else {
        setSoldeError(data.error || data.message || 'Erreur lors de l\'encaissement');
      }
    } catch (e) {
      console.error(e);
      setSoldeError('Erreur réseau');
    } finally {
      setSoldeSubmitting(false);
    }
  };

  // Ergonomie : amener la fiche client a l'ecran quand on selectionne un client
  // (liste longue/scrollee : sinon la fiche s'affiche hors champ, tout en haut)
  useEffect(() => {
    if (selectedId && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedId]);

  const handleSelect = (client) => {
    setSelectedId(client.id);
    setEditMode(false);
    setActiveTab('achats_new');
    setCompteData(null);
    setAchatsData(null);
    setSavData(null);
    setOpenForm(null);
    setFormData({});
    loadDetail(client.id);
    loadAchatsNew(client.id);   // eager : les cartes du haut lisent achatsData
  };

  const handleEdit = () => {
    setEditData({ ...detail.client });
    setEditMode(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/clients/${selectedId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        setEditMode(false);
        await loadClients();
        await loadDetail(selectedId);
      }
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newClient.nom.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClient)
      });
      if (res.ok) {
        setShowCreate(false);
        setNewClient({ nom:'', telephone:'', email:'', adresse:'', ville:'', notes:'', type_client:'particulier', nom_entreprise:'', ice:'', rc:'', if_number:'' });
        await loadClients();
      }
    } finally { setSaving(false); }
  };

  const filtered = clients.filter(c => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (c.nom||'').toLowerCase().includes(t) ||
           (c.telephone||'').includes(t) ||
           (c.email||'').toLowerCase().includes(t) ||
           (c.ville||'').toLowerCase().includes(t);
  });

  const selectedClient = clients.find(c => c.id === selectedId);

  return (
    <div className="flex flex-col md:flex-row gap-0 h-full" style={{ minHeight: '80vh' }}>

      {/* ─── COLONNE GAUCHE : liste ─── */}
      <div className={`md:w-80 w-full flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden ${selectedId ? 'hidden md:flex' : 'flex'}`}>
        {/* Header liste */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800">Clients</h2>
            <button
              onClick={() => setShowCreate(true)}
              className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              title="Ajouter un client"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Nom, tél, email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoComplete="off"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">{filtered.length} client{filtered.length > 1 ? 's' : ''}</p>
        </div>

        {/* Liste scrollable */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
          {filtered.map(client => (
            <button
              key={client.id}
              onClick={() => handleSelect(client)}
              className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 ${
                selectedId === client.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'
              }`}
            >
              <div className="relative h-9 w-9 flex-shrink-0">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                  {(client.nom||'?').charAt(0).toUpperCase()}
                </div>
                {/* REFONTE OPTION 2 2026-04-24 : dette et avoir peuvent coexister.
                    Source de verite = total_dettes_ouvertes (paiements_vente) +
                    avoir_utilisable (solde_compte post-refonte). */}
                {parseFloat(client.total_dettes_ouvertes || 0) > 0 && (
                  <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white" title="Dette facture ouverte" />
                )}
                {parseFloat(client.avoir_utilisable || 0) > 0 && (
                  <div className="absolute -top-1 -left-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" title="Avoir en compte" />
                )}
                {parseFloat(client.solde_or_grammes || 0) > 0 && (
                  <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-yellow-400 rounded-full border-2 border-white" title="Solde or" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-800 truncate">{client.nom}</p>
                  {parseFloat(client.total_dettes_ouvertes || 0) > 0 && (
                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                      DOIT {parseFloat(client.total_dettes_ouvertes).toLocaleString('fr-FR')}
                    </span>
                  )}
                  {parseFloat(client.avoir_utilisable || 0) > 0 && (
                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700">
                      AVOIR {parseFloat(client.avoir_utilisable).toLocaleString('fr-FR')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 truncate">{client.telephone || client.email || client.ville || '—'}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <UserCircle className="h-10 w-10 mx-auto mb-2" />
              <p className="text-sm">Aucun client trouvé</p>
            </div>
          )}
        </div>
      </div>

      {/* ─── PANNEAU DROIT : détail ─── */}
      <div ref={detailRef} className={`flex-1 md:ml-4 overflow-hidden ${selectedId ? 'block' : 'hidden md:block'}`}>
        {!selectedId ? (
          <div className="h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <UserCircle className="h-16 w-16 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">Sélectionnez un client</p>
              <p className="text-sm mt-1">Cliquez sur un client pour voir sa fiche</p>
            </div>
          </div>
        ) : loadingDetail ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : detail ? (
          <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '85vh' }}>
            {/* Back button mobile */}
            <button
              onClick={() => setSelectedId(null)}
              className="md:hidden flex items-center gap-2 text-blue-600 text-sm font-medium mb-1"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Retour à la liste
            </button>

            {/* ── Carte identité ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header carte */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-6 py-5 text-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-2xl bg-white bg-opacity-20 flex items-center justify-center text-3xl font-bold">
                      {(detail.client.nom||'?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      {editMode ? (
                        <input
                          value={editData.nom}
                          onChange={e => setEditData(d => ({...d, nom: e.target.value}))}
                          className="text-xl font-bold bg-white bg-opacity-20 border border-white border-opacity-40 rounded-lg px-3 py-1 text-white placeholder-white placeholder-opacity-60 w-64"
                          autoComplete="off"
                        />
                      ) : (
                        <h3 className="text-2xl font-bold">{detail.client.nom}</h3>
                      )}
                      {detail.client.ville && <p className="text-blue-200 text-sm mt-0.5">{detail.client.ville}</p>}
                      <p className="text-blue-300 text-xs mt-1">
                        Client depuis {detail.client.created_at
                          ? new Date(detail.client.created_at).toLocaleDateString('fr-FR', { month:'long', year:'numeric' })
                          : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {editMode ? (
                      <>
                        <button
                          onClick={() => setEditMode(false)}
                          className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
                        ><X className="h-5 w-5" /></button>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 bg-white text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
                        >
                          <Save className="h-4 w-4" />
                          {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleEdit}
                        className="flex items-center gap-1.5 px-3 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                        Modifier
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Infos contact */}
              <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <InfoField icon={<Phone className="h-4 w-4"/>} label="Téléphone"
                  value={editMode ? editData.telephone : detail.client.telephone}
                  edit={editMode}
                  onChange={v => setEditData(d => ({...d, telephone: v}))}
                  placeholder="06 xx xx xx xx"
                />
                <InfoField icon={<Mail className="h-4 w-4"/>} label="Email"
                  value={editMode ? editData.email : detail.client.email}
                  edit={editMode}
                  onChange={v => setEditData(d => ({...d, email: v}))}
                  placeholder="email@..."
                />
                <InfoField icon={<MapPin className="h-4 w-4"/>} label="Ville"
                  value={editMode ? editData.ville : detail.client.ville}
                  edit={editMode}
                  onChange={v => setEditData(d => ({...d, ville: v}))}
                  placeholder="Casablanca"
                />
                <InfoField icon={<MapPin className="h-4 w-4"/>} label="Adresse"
                  value={editMode ? editData.adresse : detail.client.adresse}
                  edit={editMode}
                  onChange={v => setEditData(d => ({...d, adresse: v}))}
                  placeholder="Rue, quartier..."
                />
              </div>
              {/* Entreprise fields in edit/view */}
              {(editMode || detail.client.type_client === 'entreprise') && (
                <div className="px-6 pb-4">
                  {editMode && (
                    <div className="flex items-center gap-3 mb-3">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Type :</label>
                      <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <button type="button"
                          onClick={() => setEditData(d => ({...d, type_client:'particulier'}))}
                          className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            (editData.type_client||'particulier') !== 'entreprise' ? 'bg-white shadow text-blue-700' : 'text-gray-500'
                          }`}><UserCircle className="h-3 w-3"/>Particulier</button>
                        <button type="button"
                          onClick={() => setEditData(d => ({...d, type_client:'entreprise'}))}
                          className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                            (editData.type_client||'particulier') === 'entreprise' ? 'bg-white shadow text-blue-700' : 'text-gray-500'
                          }`}><Building2 className="h-3 w-3"/>Entreprise</button>
                      </div>
                    </div>
                  )}
                  {((editMode ? editData.type_client : detail.client.type_client) === 'entreprise') && (
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                      <InfoField icon={<Building2 className="h-4 w-4"/>} label="Entreprise"
                        value={editMode ? editData.nom_entreprise : detail.client.nom_entreprise}
                        edit={editMode}
                        onChange={v => setEditData(d => ({...d, nom_entreprise: v}))}
                        placeholder="Raison sociale" />
                      <InfoField icon={<FileText className="h-4 w-4"/>} label="ICE"
                        value={editMode ? editData.ice : detail.client.ice}
                        edit={editMode}
                        onChange={v => setEditData(d => ({...d, ice: v}))}
                        placeholder="ICE" />
                      <InfoField icon={<FileText className="h-4 w-4"/>} label="RC"
                        value={editMode ? editData.rc : detail.client.rc}
                        edit={editMode}
                        onChange={v => setEditData(d => ({...d, rc: v}))}
                        placeholder="RC" />
                      <InfoField icon={<FileText className="h-4 w-4"/>} label="IF"
                        value={editMode ? editData.if_number : detail.client.if_number}
                        edit={editMode}
                        onChange={v => setEditData(d => ({...d, if_number: v}))}
                        placeholder="Identifiant fiscal" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Stats (calculees depuis achatsData = onglet Achats / ventes) ── */}
            {(() => {
              const st = computeVentesStats(achatsData);
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard icon={<ShoppingBag className="h-5 w-5 text-blue-600"/>}
                    label="Achats" value={st.nb} color="blue" />
                  <StatCard icon={<TrendingUp className="h-5 w-5 text-green-600"/>}
                    label="Total dépensé"
                    value={st.total.toLocaleString('fr-FR', {minimumFractionDigits:0}) + ' DH'}
                    color="green" />
                  <StatCard icon={<Star className="h-5 w-5 text-amber-500"/>}
                    label="Panier moyen"
                    value={st.avg.toLocaleString('fr-FR', {minimumFractionDigits:0}) + ' DH'}
                    color="amber" />
                </div>
              );
            })()}

            {/* ── Onglets ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex border-b border-gray-100 overflow-x-auto">
                {[
                  { id:'compte', label:'Compte', icon:<Wallet className="h-4 w-4"/> },
                  { id:'achats_new', label:'Achats', icon:<ShoppingBag className="h-4 w-4"/> },
                  { id:'sav', label:'SAV', icon:<Wrench className="h-4 w-4"/> },
                  { id:'fabrications', label:`Fabrications (${detail.fabrications.length})`, icon:<Gem className="h-4 w-4"/> },
                  { id:'notes', label:'Notes internes', icon:<FileText className="h-4 w-4"/> },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      if (tab.id === 'compte' && !compteData) loadCompte(selectedId);
                      if (tab.id === 'achats_new' && !achatsData) loadAchatsNew(selectedId);
                      if (tab.id === 'sav' && !savData) loadSav(selectedId);
                    }}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>

              <div className="p-4">

                {/* Onglet Compte */}
                {activeTab === 'compte' && (
                  <div className="space-y-4">
                    {/* Badges solde — REFONTE OPTION 2 2026-04-24 : avoir + dette coexistent */}
                    <div className="flex flex-wrap gap-3">
                      {parseFloat(detail.client.avoir_utilisable ?? detail.client.solde_compte ?? 0) > 0 && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-green-100 text-green-800">
                          <CircleDollarSign className="h-4 w-4" />
                          Avoir: {Number(detail.client.avoir_utilisable ?? detail.client.solde_compte ?? 0).toLocaleString('fr-FR')} DH
                        </div>
                      )}
                      {parseFloat(detail.client.total_dettes_ouvertes || 0) > 0 && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-red-100 text-red-800">
                          <CircleDollarSign className="h-4 w-4" />
                          Dette factures: {Number(detail.client.total_dettes_ouvertes).toLocaleString('fr-FR')} DH
                        </div>
                      )}
                      {/* Legacy : solde_compte negatif (pre-refonte) reste affiche */}
                      {parseFloat(detail.client.solde_compte || 0) < 0 && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-red-100 text-red-800">
                          <CircleDollarSign className="h-4 w-4" />
                          Solde legacy: {Number(detail.client.solde_compte).toLocaleString('fr-FR')} DH
                        </div>
                      )}
                      {parseFloat(detail.client.avoir_utilisable ?? detail.client.solde_compte ?? 0) === 0 &&
                       parseFloat(detail.client.total_dettes_ouvertes || 0) === 0 && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-gray-100 text-gray-600">
                          <CircleDollarSign className="h-4 w-4" />
                          Compte a 0
                        </div>
                      )}
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-amber-100 text-amber-800">
                        <Weight className="h-4 w-4" />
                        Or: {Number(detail.client.solde_or_grammes || 0).toFixed(3)} g
                      </div>
                      <label className="inline-flex items-center gap-2 ml-auto cursor-pointer">
                        <span className="text-xs text-gray-500">Compte actif</span>
                        <input type="checkbox" checked={!!detail.client.compte_actif}
                          onChange={async (e) => {
                            try {
                              await fetch(`${API}/clients/${selectedId}`, {
                                method:'PUT', headers: getHeaders(),
                                body: JSON.stringify({ compte_actif: e.target.checked ? 1 : 0 })
                              });
                              loadDetail(selectedId);
                            } catch(err) { console.error(err); }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      </label>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => { setOpenForm(openForm === 'depot_or' ? null : 'depot_or'); setFormData({}); }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          openForm === 'depot_or' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                        }`}><Weight className="h-4 w-4"/>Deposer or</button>
                      <button onClick={() => { setOpenForm(openForm === 'depot_argent' ? null : 'depot_argent'); setFormData({ mode_paiement: 'especes' }); }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          openForm === 'depot_argent' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}><CircleDollarSign className="h-4 w-4"/>Deposer argent (avoir)</button>
                      <button
                        onClick={() => { setOpenForm(openForm === 'encaisser_dette' ? null : 'encaisser_dette'); setFormData({ mode_paiement: 'especes' }); }}
                        disabled={parseFloat(detail.client.solde_compte || 0) >= 0}
                        title={parseFloat(detail.client.solde_compte || 0) >= 0 ? 'Pas de dette à encaisser' : 'Encaisser le remboursement de la dette'}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          openForm === 'encaisser_dette' ? 'bg-red-600 text-white border-red-600' : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}><CheckCircle2 className="h-4 w-4"/>Encaisser dette</button>
                      <button
                        onClick={() => { setOpenForm(openForm === 'retirer_avoir' ? null : 'retirer_avoir'); setFormData({ mode_paiement: 'especes' }); }}
                        disabled={parseFloat(detail.client.solde_compte || 0) <= 0}
                        title={parseFloat(detail.client.solde_compte || 0) <= 0 ? 'Pas d\'avoir à retirer' : 'Le client reprend son avoir en caisse'}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          openForm === 'retirer_avoir' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}><Wallet className="h-4 w-4"/>Retirer avoir</button>
                      <button onClick={() => { setOpenForm(openForm === 'credit' ? null : 'credit'); setFormData({}); }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          openForm === 'credit' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}><AlertCircle className="h-4 w-4"/>Mettre en credit</button>
                    </div>

                    {/* Inline forms */}
                    {openForm === 'depot_or' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm font-semibold text-amber-800">Depot d'or</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Poids (g)</label>
                            <input type="number" step="0.001" value={formData.poids_or||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, poids_or: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="0.000" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Prix/g (DH)</label>
                            <input type="number" step="0.01" value={formData.prix_gramme||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, prix_gramme: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="0.00" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Notes</label>
                            <input type="text" value={formData.notes||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, notes: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Optionnel" />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setOpenForm(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                          <button onClick={() => handleCompteAction('depot_or')} disabled={submitting || !formData.poids_or}
                            className="px-4 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
                            {submitting ? 'Envoi...' : 'Valider'}
                          </button>
                        </div>
                      </div>
                    )}

                    {openForm === 'depot_argent' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm font-semibold text-green-800">Depot d'argent (création d'avoir)</p>
                        <p className="text-xs text-gray-600">Le client remet de l'argent → crédit en avoir sur son compte.</p>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Boutique</label>
                            <select value={formData.boutique_id || ''}
                              onChange={e => setFormData(d=>({...d, boutique_id: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                              <option value="">— Choisir —</option>
                              {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Montant (DH) *</label>
                            <input type="number" step="0.01" value={formData.montant||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, montant: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="0.00" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Mode paiement *</label>
                            <select value={formData.mode_paiement || 'especes'}
                              onChange={e => setFormData(d=>({...d, mode_paiement: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                              <option value="especes">Especes</option>
                              <option value="carte">Carte</option>
                              <option value="virement">Virement</option>
                              <option value="cheque">Cheque</option>
                              <option value="autre">Autre</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Notes</label>
                            <input type="text" value={formData.notes||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, notes: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Optionnel" />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setOpenForm(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                          <button onClick={() => handleCompteAction('depot_argent')} disabled={submitting || !formData.montant || !formData.mode_paiement}
                            className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                            {submitting ? 'Envoi...' : 'Valider'}
                          </button>
                        </div>
                      </div>
                    )}

                    {openForm === 'encaisser_dette' && (() => {
                      const dette = Math.abs(parseFloat(detail.client.solde_compte || 0));
                      return (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                          <p className="text-sm font-semibold text-red-800">Encaisser la dette du client</p>
                          <p className="text-xs text-gray-600">
                            Dette actuelle : <b>{dette.toLocaleString('fr-FR')} DH</b>.
                          </p>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Boutique *</label>
                              <select value={formData.boutique_id || ''}
                                onChange={e => setFormData(d=>({...d, boutique_id: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                                <option value="">— Choisir —</option>
                                {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Montant encaissé (DH) *</label>
                              <input type="number" step="0.01" value={formData.montant||''} autoComplete="off"
                                onChange={e => setFormData(d=>({...d, montant: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder={dette.toFixed(2)} />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Mode paiement *</label>
                              <select value={formData.mode_paiement || 'especes'}
                                onChange={e => setFormData(d=>({...d, mode_paiement: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                                <option value="especes">Especes</option>
                                <option value="carte">Carte</option>
                                <option value="virement">Virement</option>
                                <option value="cheque">Cheque</option>
                                <option value="autre">Autre</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" value={formData.notes||''} autoComplete="off"
                                onChange={e => setFormData(d=>({...d, notes: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Optionnel" />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setOpenForm(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                            <button onClick={() => handleCompteAction('encaisser_dette')} disabled={submitting || !formData.montant || !formData.mode_paiement || !formData.boutique_id}
                              className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                              {submitting ? 'Envoi...' : 'Encaisser'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {openForm === 'retirer_avoir' && (() => {
                      const avoir = parseFloat(detail.client.solde_compte || 0);
                      const montantSaisi = parseFloat(formData.montant || 0);
                      const depasse = montantSaisi > avoir;
                      return (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                          <p className="text-sm font-semibold text-emerald-800">Retirer l'avoir du client</p>
                          <p className="text-xs text-gray-600">
                            Avoir disponible : <b>{avoir.toLocaleString('fr-FR')} DH</b>.
                            Le client reprend son argent → sortie caisse.
                          </p>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Boutique *</label>
                              <select value={formData.boutique_id || ''}
                                onChange={e => setFormData(d=>({...d, boutique_id: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                                <option value="">— Choisir —</option>
                                {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Montant à retirer (DH) *</label>
                              <input type="number" step="0.01" max={avoir} value={formData.montant||''} autoComplete="off"
                                onChange={e => setFormData(d=>({...d, montant: e.target.value}))}
                                className={`w-full px-2 py-1.5 border rounded-lg text-sm ${depasse ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                                placeholder={avoir.toFixed(2)} />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Mode paiement *</label>
                              <select value={formData.mode_paiement || 'especes'}
                                onChange={e => setFormData(d=>({...d, mode_paiement: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                                <option value="especes">Especes</option>
                                <option value="carte">Carte</option>
                                <option value="virement">Virement</option>
                                <option value="cheque">Cheque</option>
                                <option value="autre">Autre</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" value={formData.notes||''} autoComplete="off"
                                onChange={e => setFormData(d=>({...d, notes: e.target.value}))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Optionnel" />
                            </div>
                          </div>
                          {depasse && (
                            <p className="text-xs text-red-700 bg-red-100 px-3 py-2 rounded">
                              ⚠ Le montant dépasse l'avoir disponible.
                            </p>
                          )}
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setOpenForm(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                            <button onClick={() => handleCompteAction('retirer_avoir')}
                              disabled={submitting || !formData.montant || !formData.mode_paiement || !formData.boutique_id || depasse || montantSaisi <= 0}
                              className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                              {submitting ? 'Envoi...' : 'Retirer'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {openForm === 'credit' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm font-semibold text-red-800">Mettre en credit</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Montant (DH)</label>
                            <input type="number" step="0.01" value={formData.montant||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, montant: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="0.00" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Description</label>
                            <input type="text" value={formData.description||''} autoComplete="off"
                              onChange={e => setFormData(d=>({...d, description: e.target.value}))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Motif du credit" />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setOpenForm(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                          <button onClick={() => handleCompteAction('credit')} disabled={submitting || !formData.montant}
                            className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                            {submitting ? 'Envoi...' : 'Valider'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Historique mouvements */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Historique mouvements</p>
                      {!compteData ? (
                        <p className="text-center py-6 text-gray-400 text-sm">Chargement...</p>
                      ) : (!compteData.mouvements || compteData.mouvements.length === 0) ? (
                        <p className="text-center py-6 text-gray-400 text-sm">Aucun mouvement</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-right px-3 py-2">Montant</th>
                                <th className="text-right px-3 py-2">Or (g)</th>
                                <th className="text-left px-3 py-2">Description</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {compteData.mouvements.map((m, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-600">{m.date ? new Date(m.date).toLocaleDateString('fr-FR') : '—'}</td>
                                  <td className="px-3 py-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      (m.type||'').includes('depot') ? 'bg-green-100 text-green-700' :
                                      (m.type||'').includes('credit') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                    }`}>{m.type || '—'}</span>
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium">{m.montant ? Number(m.montant).toLocaleString('fr-FR') + ' DH' : '—'}</td>
                                  <td className="px-3 py-2 text-right text-amber-700 font-medium">{m.poids_or ? Number(m.poids_or).toFixed(3) + ' g' : '—'}</td>
                                  <td className="px-3 py-2 text-gray-500 truncate max-w-[200px]">{m.description || m.notes || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Onglet Achats (new endpoint) */}
                {activeTab === 'achats_new' && (
                  <div className="space-y-4">
                    {!achatsData ? (
                      <p className="text-center py-8 text-gray-400 text-sm">Chargement...</p>
                    ) : achatsData.length === 0 && mouvementsCompte.length === 0 ? (
                      <div className="text-center py-10">
                        <ShoppingBag className="h-10 w-10 mx-auto mb-2 text-gray-200" />
                        <p className="text-gray-400 text-sm">Aucun achat</p>
                      </div>
                    ) : (
                      <>
                        {/* Liste des achats */}
                        {achatsData.length > 0 && (
                          <div>
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Achats ({achatsData.length})</h4>
                            <div className="space-y-2">
                              {achatsData.map((a, i) => {
                                const montantCompte = parseFloat(a.montant_compte_deduit || 0);
                                const paiements = a.paiements || [];
                                return (
                                  <div key={i} className="bg-white rounded-lg border p-3 hover:bg-gray-50">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-mono text-xs font-bold bg-gray-100 px-2 py-0.5 rounded">{a.id_produit || a.reference_article || '—'}</span>
                                          <span className="font-medium text-gray-900">{a.nom || '—'}</span>
                                          {a.categorie && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{a.categorie}</span>}
                                          {a.couleur_or && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Or {a.couleur_or}</span>}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {a.date_vente ? new Date(a.date_vente).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }) : '—'}
                                          {a.boutique_nom && <span> — {a.boutique_nom}</span>}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-lg font-black text-gray-900">{Number(a.prix_vente || 0).toLocaleString('fr-FR')} DH</p>
                                        {parseFloat(a.montant_final || 0) !== parseFloat(a.prix_vente || 0) && (
                                          <p className="text-xs text-green-600 font-medium">Encaisse: {Number(a.montant_final || 0).toLocaleString('fr-FR')} DH</p>
                                        )}
                                      </div>
                                    </div>
                                    {/* Detail paiements */}
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {paiements.length > 0 ? paiements.map((p, j) => (
                                        <span key={j} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
                                          {p.mode === 'especes' ? 'Esp.' : p.mode === 'carte' ? 'CB' : p.mode === 'cheque' ? 'Chq' : p.mode === 'virement' ? 'Vir.' : p.mode === 'or' ? 'Or' : p.mode === 'compte' ? 'Cpt' : p.mode} {Number(p.montant).toLocaleString('fr-FR')}
                                        </span>
                                      )) : (
                                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{a.mode_paiement || '—'}</span>
                                      )}
                                      {parseFloat(a.remise_montant || 0) > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">Remise: -{Number(a.remise_montant).toLocaleString('fr-FR')}</span>
                                      )}
                                      {parseFloat(a.montant_or_deduit || 0) > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">Or: {Number(a.montant_or_deduit).toLocaleString('fr-FR')} ({a.poids_or_deduit}g)</span>
                                      )}
                                      {montantCompte > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">Mis en compte: {Number(montantCompte).toLocaleString('fr-FR')} DH</span>
                                      )}
                                    </div>
                                    {/* Patch 42 — Dette restante + bouton Encaisser solde */}
                                    {parseFloat(a.dette_restante || 0) > 0.01 && (
                                      <div className="mt-2 pt-2 border-t border-red-100 flex items-center justify-between gap-2">
                                        <span className="text-xs px-2 py-1 rounded bg-red-600 text-white font-bold flex items-center gap-1">
                                          <AlertCircle className="h-3 w-3" />
                                          Dette: {Number(a.dette_restante).toLocaleString('fr-FR')} DH
                                          {a.statut_paiement === 'partiel' && <span className="ml-1 opacity-80">(partiel)</span>}
                                        </span>
                                        <button
                                          onClick={() => openSoldeModal(a)}
                                          className="text-xs bg-green-600 hover:bg-green-700 text-white font-semibold px-3 py-1 rounded flex items-center gap-1"
                                        >
                                          <CheckCircle2 className="h-3 w-3" />
                                          Encaisser solde
                                        </button>
                                      </div>
                                    )}
                                    {a.statut_paiement === 'soldee' && parseFloat(a.montant_compte_deduit || 0) > 0 && (
                                      <div className="mt-2 pt-2 border-t border-green-100">
                                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 font-medium flex items-center gap-1 w-fit">
                                          <CheckCircle2 className="h-3 w-3" /> Facture soldée
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Mouvements de compte lies */}
                        {mouvementsCompte.length > 0 && (
                          <div>
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Mouvements de compte ({mouvementsCompte.length})</h4>
                            <div className="space-y-1">
                              {mouvementsCompte.map((m, i) => {
                                const montant = parseFloat(m.montant || 0);
                                // REFONTE OPTION 2 2026-04-24 : simplification. Le signe
                                // du montant est la seule source de verite (sortie = negatif).
                                const isDebit = montant < 0;
                                return (
                                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${isDebit ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                                    <div>
                                      <p className="text-sm font-medium text-gray-800">{m.description || m.type_mouvement}</p>
                                      <p className="text-xs text-gray-500">{m.created_at ? new Date(m.created_at).toLocaleDateString('fr-FR') : '—'}</p>
                                    </div>
                                    <p className={`font-bold ${isDebit ? 'text-red-700' : 'text-green-700'}`}>
                                      {montant > 0 ? '+' : ''}{Number(montant).toLocaleString('fr-FR')} DH
                                      {parseFloat(m.poids_or || 0) > 0 && <span className="text-xs text-yellow-600 ml-1">({m.poids_or}g or)</span>}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Onglet SAV */}
                {activeTab === 'sav' && (
                  <div>
                    {!savData ? (
                      <p className="text-center py-8 text-gray-400 text-sm">Chargement...</p>
                    ) : savData.length === 0 ? (
                      <div className="text-center py-10">
                        <Wrench className="h-10 w-10 mx-auto mb-2 text-gray-200" />
                        <p className="text-gray-400 text-sm">Aucun service SAV</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                              <th className="text-left px-3 py-2">Numero</th>
                              <th className="text-left px-3 py-2">Type</th>
                              <th className="text-left px-3 py-2">Statut</th>
                              <th className="text-left px-3 py-2">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {savData.map((s, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <span className="font-mono text-xs font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{s.numero || s.id || '—'}</span>
                                </td>
                                <td className="px-3 py-2 text-gray-700">{s.type_service || s.type || '—'}</td>
                                <td className="px-3 py-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    (s.statut||'').toLowerCase().includes('termin') ? 'bg-green-100 text-green-700' :
                                    (s.statut||'').toLowerCase().includes('cours') ? 'bg-blue-100 text-blue-700' :
                                    (s.statut||'').toLowerCase().includes('attent') ? 'bg-amber-100 text-amber-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>{s.statut || '—'}</span>
                                </td>
                                <td className="px-3 py-2 text-gray-600">{s.date_reception ? new Date(s.date_reception).toLocaleDateString('fr-FR') : s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Fabrications */}
                {activeTab === 'fabrications' && (
                  detail.fabrications.length === 0 ? (
                    <p className="text-center py-8 text-gray-400 text-sm">Aucune fabrication sur commande pour ce client</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.fabrications.map((f, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                              f.inventaire_actif ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              <Package className={`h-5 w-5 ${f.inventaire_actif ? 'text-green-600' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-bold text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded">
                                  {f.reference_article || f.id_produit}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  f.inventaire_actif
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {f.inventaire_actif ? 'En stock' : f.client_acheteur ? `Vendu · ${f.client_acheteur}` : 'Vendu'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{f.nom || f.categorie} {f.couleur_or ? `· Or ${f.couleur_or.toLowerCase()}` : ''}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            {f.pvp_magasin && (
                              <p className="font-bold text-gray-800">{Number(f.pvp_magasin).toLocaleString('fr-FR')} DH</p>
                            )}
                            {f.boutique_nom && <p className="text-xs text-blue-500">{f.boutique_nom}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* Notes internes */}
                {activeTab === 'notes' && (
                  <div>
                    <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5" />
                      Notes internes — visible uniquement par l'équipe
                    </p>
                    {editMode ? (
                      <textarea
                        value={editData.notes || ''}
                        onChange={e => setEditData(d => ({...d, notes: e.target.value}))}
                        rows={8}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 resize-y"
                        placeholder="Ex: Négocie souvent les prix, préfère l'or rose, anniversaire en mars, VIP..."
                        autoComplete="off"
                      />
                    ) : (
                      <div
                        onClick={handleEdit}
                        className={`min-h-[120px] px-4 py-3 rounded-xl border-2 border-dashed cursor-text transition-colors ${
                          detail.client.notes
                            ? 'border-gray-200 bg-amber-50 text-gray-700 text-sm leading-relaxed'
                            : 'border-gray-200 text-gray-400 text-sm flex items-center justify-center hover:border-blue-300'
                        }`}
                      >
                        {detail.client.notes || 'Cliquez sur Modifier pour ajouter des notes...'}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ─── Modal création client ─── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2"><Plus className="h-5 w-5"/>Nouveau client</h3>
              <button onClick={() => setShowCreate(false)} className="hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors"><X className="h-5 w-5"/></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Toggle Particulier / Entreprise */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Type de client</label>
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button type="button"
                    onClick={() => setNewClient(n => ({...n, type_client:'particulier'}))}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      newClient.type_client !== 'entreprise' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'
                    }`}><UserCircle className="h-4 w-4"/>Particulier</button>
                  <button type="button"
                    onClick={() => setNewClient(n => ({...n, type_client:'entreprise'}))}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      newClient.type_client === 'entreprise' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'
                    }`}><Building2 className="h-4 w-4"/>Entreprise</button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Nom complet *</label>
                <input type="text" autoComplete="off" value={newClient.nom}
                  onChange={e => setNewClient(n => ({...n, nom: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Amina Benali" />
              </div>
              {newClient.type_client === 'entreprise' && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Nom entreprise</label>
                    <input type="text" autoComplete="off" value={newClient.nom_entreprise}
                      onChange={e => setNewClient(n => ({...n, nom_entreprise: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Raison sociale" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">ICE</label>
                      <input type="text" autoComplete="off" value={newClient.ice}
                        onChange={e => setNewClient(n => ({...n, ice: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="ICE" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">RC</label>
                      <input type="text" autoComplete="off" value={newClient.rc}
                        onChange={e => setNewClient(n => ({...n, rc: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="RC" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">IF</label>
                      <input type="text" autoComplete="off" value={newClient.if_number}
                        onChange={e => setNewClient(n => ({...n, if_number: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Identifiant fiscal" />
                    </div>
                  </div>
                </>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Téléphone</label>
                  <input type="tel" autoComplete="off" value={newClient.telephone}
                    onChange={e => setNewClient(n => ({...n, telephone: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="06 xx xx xx xx" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Email</label>
                  <input type="email" autoComplete="off" value={newClient.email}
                    onChange={e => setNewClient(n => ({...n, email: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="email@..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Ville</label>
                  <input type="text" autoComplete="off" value={newClient.ville}
                    onChange={e => setNewClient(n => ({...n, ville: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Casablanca" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Adresse</label>
                  <input type="text" autoComplete="off" value={newClient.adresse}
                    onChange={e => setNewClient(n => ({...n, adresse: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Rue, quartier..." />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Notes internes</label>
                <textarea value={newClient.notes}
                  onChange={e => setNewClient(n => ({...n, notes: e.target.value}))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Observations, préférences, profil..." />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm">Annuler</button>
                <button onClick={handleCreate} disabled={saving || !newClient.nom.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50">
                  {saving ? 'Création...' : 'Créer le client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patch 42/43 — Modal Encaisser solde (avec mode avoir Phase 2) */}
      {soldeVente && (() => {
        const soldeCompte = parseFloat(detail?.client?.solde_compte || 0);
        const avoirDispo = soldeCompte > 0 ? soldeCompte : 0;
        const isAvoir = soldeForm.mode_paiement === 'avoir';
        const dette = parseFloat(soldeVente.dette_restante || 0);
        const maxMontant = isAvoir ? Math.min(dette, avoirDispo) : dette;
        const montantSaisi = parseFloat(soldeForm.montant || 0);
        const btnLabel = isAvoir
          ? `Solder avec avoir ${Number(montantSaisi || 0).toLocaleString('fr-FR')} MAD`
          : 'Confirmer encaissement';
        const btnDisabled = soldeSubmitting
          || !soldeForm.montant
          || !soldeForm.mode_paiement
          || (!isAvoir && !soldeForm.boutique_id)
          || montantSaisi <= 0
          || montantSaisi > maxMontant + 0.01;
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Encaisser solde
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {soldeVente.nom || 'Bijou'} — {soldeVente.id_produit || soldeVente.reference_article || ''}
                </p>
              </div>
              <button onClick={closeSoldeModal} className="text-gray-400 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Cards résumé : Dette + Avoir dispo */}
              <div className={`grid gap-3 ${avoirDispo > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-700 font-medium mb-1">Reste dû</p>
                  <p className="text-lg font-black text-red-700">
                    {Number(dette).toLocaleString('fr-FR')} MAD
                  </p>
                </div>
                {avoirDispo > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-700 font-medium mb-1">Avoir client</p>
                    <p className="text-lg font-black text-green-700">
                      {Number(avoirDispo).toLocaleString('fr-FR')} MAD
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Montant à encaisser *</label>
                  <input
                    type="number" step="0.01" min="0.01"
                    max={maxMontant}
                    value={soldeForm.montant}
                    onChange={e => setSoldeForm(f => ({...f, montant: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {isAvoir
                      ? `Max ${Number(maxMontant).toLocaleString('fr-FR')} MAD (min dette/avoir)`
                      : 'Paiement partiel autorisé'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Mode paiement *</label>
                  <select
                    value={soldeForm.mode_paiement || 'especes'}
                    onChange={e => {
                      const newMode = e.target.value;
                      // UX 2026-04-24 : auto-cap du montant a min(dette, avoir)
                      // en mode avoir, retablit dette totale pour les modes cash.
                      const cap = newMode === 'avoir' ? Math.min(dette, avoirDispo) : dette;
                      setSoldeForm(f => ({...f, mode_paiement: newMode, montant: cap}));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="especes">Espèces</option>
                    <option value="carte">Carte</option>
                    <option value="virement">Virement</option>
                    <option value="cheque">Chèque</option>
                    <option value="autre">Autre</option>
                    {avoirDispo > 0 && (
                      <option value="avoir">
                        Utiliser avoir ({Number(avoirDispo).toLocaleString('fr-FR')} dispo)
                      </option>
                    )}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isAvoir ? 'text-gray-400' : 'text-gray-600'}`}>
                    Boutique {isAvoir ? <span className="text-gray-400">(non requis)</span> : '*'}
                  </label>
                  <select
                    value={soldeForm.boutique_id || ''}
                    onChange={e => setSoldeForm(f => ({...f, boutique_id: e.target.value}))}
                    disabled={isAvoir}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
                      isAvoir ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300'
                    }`}
                  >
                    <option value="">— Choisir —</option>
                    {boutiques.map(b => (
                      <option key={b.id} value={b.id}>{b.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Référence</label>
                  <input
                    type="text"
                    value={soldeForm.reference || ''}
                    onChange={e => setSoldeForm(f => ({...f, reference: e.target.value}))}
                    placeholder="n° chèque, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Commentaire</label>
                <input
                  type="text"
                  value={soldeForm.commentaire || ''}
                  onChange={e => setSoldeForm(f => ({...f, commentaire: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              {isAvoir && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Mode <b>avoir</b> : ne touche pas la caisse, débite le solde client.
                    L'avoir du compte sera consommé pour solder la facture.
                  </span>
                </div>
              )}
              {soldeError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-700 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" /> {soldeError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl">
              <button onClick={closeSoldeModal} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
                Annuler
              </button>
              <button
                onClick={handleEncaisserSolde}
                disabled={btnDisabled}
                className={`px-6 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-2 ${
                  isAvoir ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <CheckCircle2 className="h-4 w-4" />
                {soldeSubmitting ? 'Encaissement...' : btnLabel}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

/* ── Composants utilitaires ── */

function InfoField({ icon, label, value, edit, onChange, placeholder }) {
  return (
    <div>
      <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">{icon}{label}</p>
      {edit ? (
        <input
          type="text"
          autoComplete="off"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
        />
      ) : (
        <p className="text-sm font-medium text-gray-800">{value || <span className="text-gray-300">—</span>}</p>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue:  'bg-blue-50 border-blue-100',
    green: 'bg-green-50 border-green-100',
    amber: 'bg-amber-50 border-amber-100',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || 'bg-white border-gray-100'}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span></div>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  );
}

export default ClientsAvance;
